import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, MapPin, Smartphone, Battery, Cpu, Wrench, ShieldCheck, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const Home = () => {
  const services = [
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Screen Repair",
      description: "Lifetime warranty on all Apple screen replacements"
    },
    {
      icon: <Battery className="w-8 h-8" />,
      title: "Battery Replacement",
      description: "Restore your device's battery life to like-new condition"
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "Microsoldering",
      description: "Advanced board-level repairs for complex issues"
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "MacBook Upgrades",
      description: "SSD upgrades and hardware enhancements"
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: "Data Recovery",
      description: "Safe and secure recovery of your important files"
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Custom Gaming PCs",
      description: "Built from scratch with premium components"
    }
  ];

  const trustSignals = [
    { icon: <Clock className="w-5 h-5" />, text: "Same-Day Service" },
    { icon: <ShieldCheck className="w-5 h-5" />, text: "Lifetime Warranty" },
    { icon: <Star className="w-5 h-5" />, text: "Expert Technicians" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50/30">
      {/* Hero Section */}
      <section className="py-8 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            
            {/* Left Column - Value Proposition */}
            <div className="col-span-12 lg:col-span-6 space-y-8">
              {/* Eyebrow */}
              <div className="text-center lg:text-left">
                <span className="inline-block text-sm text-sky-600 font-semibold uppercase tracking-[0.2em] bg-sky-50 px-4 py-2 rounded-full">
                  New Hampshire's #1 Trusted Electronics Repair Shop
                </span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-tight text-slate-900">
                Meet Your Electronics
                <span className="block text-sky-500 mt-2">Apple Repair Team</span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
                Hi, we're the team behind Apple Tech Guys NH. We're here to fix your devices with expert care, 
                honest service, and a lifetime warranty you can trust.
              </p>

              {/* Trust Signals */}
              <div className="flex flex-wrap gap-4">
                {trustSignals.map((signal) => (
                  <div 
                    key={signal.text}
                    className="flex items-center gap-2 bg-white rounded-full px-4 py-2 border border-slate-200 shadow-sm"
                  >
                    <div className="text-sky-500">{signal.icon}</div>
                    <span className="text-sm font-medium text-slate-700">{signal.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  asChild
                  className="rounded-full bg-sky-500 text-white px-8 py-6 text-lg font-semibold hover:bg-sky-600 transition-all hover:scale-[1.02] active:scale-95 shadow-lg shadow-sky-500/20"
                  data-testid="hero-book-cta"
                >
                  <a href="tel:+19782576427">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now: (978) 257-6427
                  </a>
                </Button>
                
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full bg-white text-slate-800 px-8 py-6 text-lg font-semibold hover:bg-slate-50 transition-all hover:scale-[1.02] active:scale-95 border-2 border-slate-200"
                >
                  <Link to="/contact">View Our Work</Link>
                </Button>
              </div>
            </div>

            {/* Right Column - Hero Image */}
            <div className="col-span-12 lg:col-span-6">
              <div className="relative group">
                {/* Main Image Container - Bigger with proper pop effect */}
                <div className="relative rounded-[3rem] overflow-hidden transition-all duration-500 group-hover:scale-[1.03]"
                     style={{
                       boxShadow: '0 30px 80px -10px rgba(71, 85, 105, 0.4), 0 20px 40px -10px rgba(71, 85, 105, 0.3), 0 10px 20px -5px rgba(71, 85, 105, 0.2)',
                       border: '6px solid rgba(226, 232, 240, 0.9)',
                       transform: 'translateZ(0)'
                     }}>
                  <img
                    src="https://customer-assets-wrfwihn1.emergentagent.net/job_83cd0194-c314-4ffc-99ab-72d210cfce32/artifacts/5nvqquz0_IMG_1868.PNG"
                    alt="Apple Tech Guys NH - Your trusted repair experts"
                    className="w-full h-auto object-cover"
                    style={{ 
                      filter: 'brightness(1.05) contrast(1.05)',
                      transform: 'scale(1.01)'
                    }}
                  />
                  
                  {/* Subtle gradient overlay for depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/5 to-transparent pointer-events-none"></div>
                  
                  {/* Inner glow effect */}
                  <div className="absolute inset-0 pointer-events-none" 
                       style={{
                         boxShadow: 'inset 0 0 30px rgba(255, 255, 255, 0.1)'
                       }}>
                  </div>
                </div>
                
                {/* Outer glow for extra pop */}
                <div className="absolute inset-0 -z-10 rounded-[3rem]" 
                     style={{
                       background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(148, 163, 184, 0.15))',
                       filter: 'blur(30px)',
                       transform: 'scale(1.02)'
                     }}>
                </div>

                {/* Google Reviews Badge - Centered Below Photo */}
                <div className="flex justify-center mt-6">
                  <a 
                    href="https://www.google.com/maps/place/Apple+Tech+Guys+-+NH+iPhone+Repair+Service+-+Fix+My+Apple+MacBooks+Same+Day+Repairs/@43.1912677,-71.5260564,17z/data=!4m8!3m7!1s0x89e26b2a79f1bc7b:0x647e9dcc0483b02b!8m2!3d43.1912677!4d-71.5260564!9m1!1b1!16s%2Fg%2F11fw_3lz5y"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg hover:shadow-xl transition-all hover:scale-105 border border-slate-200"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 48 48">
                      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
                      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
                    </svg>
                    <div className="text-left">
                      <div className="flex items-center gap-1">
                        <span className="text-xl font-bold text-slate-900">4.9</span>
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <div className="text-sm text-slate-600">332 Google Reviews</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12 lg:mb-16 relative">
            {/* Left Wrench Icon */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 hidden xl:block">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/83cd0194-c314-4ffc-99ab-72d210cfce32/images/bafafde66f4fbee106085126aaeb4405370a39525de075161fc2f20e1a89de54.jpeg"
                alt="Repair Icon"
                className="h-24 w-24 object-contain opacity-60"
              />
            </div>
            
            <span className="text-sm text-sky-600 font-semibold uppercase tracking-[0.2em]">Our Services</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
              Expert Repairs & Upgrades
            </h2>
            <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
              From simple screen replacements to complex microsoldering, we've got you covered.
            </p>
            
            {/* Right Wrench Icon */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden xl:block">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/83cd0194-c314-4ffc-99ab-72d210cfce32/images/bafafde66f4fbee106085126aaeb4405370a39525de075161fc2f20e1a89de54.jpeg"
                alt="Repair Icon"
                className="h-24 w-24 object-contain opacity-60 scale-x-[-1]"
              />
            </div>
          </div>

          {/* Bento Grid Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div
                key={service.title}
                className="bg-white rounded-[2rem] border border-slate-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 group"
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-sky-50 flex items-center justify-center text-sky-600 mb-6 group-hover:bg-sky-100 transition-colors">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-sky-50 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Content */}
            <div className="space-y-6">
              <span className="text-sm text-sky-600 font-semibold uppercase tracking-[0.2em]">Why Choose Us</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
                Quality Repairs You Can Trust
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                We're not just another repair shop. We're your neighbors, committed to providing honest, 
                expert service with a personal touch. Every device is treated with care, and every repair 
                is backed by our lifetime warranty.
              </p>
              
              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <ShieldCheck className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Lifetime Warranty</h4>
                    <p className="text-slate-600">All Apple screen repairs come with our lifetime warranty for your peace of mind.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <Clock className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Fast Turnaround</h4>
                    <p className="text-slate-600">Most repairs completed the same day, so you're never without your device for long.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <Star className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Expert Technicians</h4>
                    <p className="text-slate-600">Certified professionals with years of experience in Apple device repairs.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Price Match Guarantee</h4>
                    <p className="text-slate-600">We'll match any local repair shop's pricing on cell phone screen repairs.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-white rounded-[2.5rem] p-8 lg:p-12 shadow-xl border border-slate-100">
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-4xl lg:text-5xl font-bold text-sky-500 mb-2">9500+</div>
                  <div className="text-sm text-slate-600 uppercase tracking-wide">Devices Repaired</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl lg:text-5xl font-bold text-sky-500 mb-2">99%</div>
                  <div className="text-sm text-slate-600 uppercase tracking-wide">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl lg:text-5xl font-bold text-sky-500 mb-2">Same Day</div>
                  <div className="text-sm text-slate-600 uppercase tracking-wide">Most Repairs</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl lg:text-5xl font-bold text-sky-500 mb-2">5★</div>
                  <div className="text-sm text-slate-600 uppercase tracking-wide">Customer Reviews</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* NO FIX NO FEE Policy Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-[2.5rem] p-8 md:p-12 shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              {/* Badge */}
              <div className="flex justify-center lg:justify-start">
                <div className="bg-white rounded-2xl px-8 py-6 shadow-xl">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-sky-600">NO FIX</div>
                    <div className="text-3xl font-bold text-slate-900">NO FEE*</div>
                    <div className="text-xs text-slate-500 mt-3">*Diagnostic fee only if<br/>repair is declined</div>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="lg:col-span-2">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Fair & Transparent Pricing
                </h2>
                <p className="text-lg text-white/95 leading-relaxed">
                  Not sure what's wrong with your device? No problem. We'll diagnose it first. 
                  <span className="font-semibold"> We fix almost everything that comes through our door.</span> If we successfully repair your device, 
                  you only pay the flat repair fee—no diagnostic charge. If you decide not to proceed with the repair, 
                  there's just a small diagnostic fee. <span className="font-semibold">Our goal: fix your device right, at a fair price.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-[2.5rem] p-8 md:p-12 lg:p-16 shadow-2xl shadow-sky-500/20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to Fix Your Device?
            </h2>
            <p className="text-lg md:text-xl text-sky-50 mb-8 max-w-2xl mx-auto leading-relaxed">
              Get in touch today for a free quote. We'll have your device working like new in no time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="rounded-full bg-white text-sky-600 px-8 py-6 text-lg font-semibold hover:bg-sky-50 transition-all hover:scale-[1.02] active:scale-95 shadow-xl"
              >
                <a href="tel:+19782576427">
                  <Phone className="w-5 h-5 mr-2" />
                  (978) 257-6427
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full bg-transparent text-white px-8 py-6 text-lg font-semibold hover:bg-white/10 transition-all hover:scale-[1.02] active:scale-95 border-2 border-white"
              >
                <Link to="/contact">Send Us a Message</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
